
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useUserContext } from "../UserContext";
import FormHelper from "../../helpers/FormHelper";
import PartnerPmsService from "../../services/PartnerPmsService";
import { IBlock, IExpectedOccupancy, IFloor, IReservation, IRoom, IRoomType } from "../../interfaces/IPartnerPms";
import { GovernanceStatusData, GovernanceStatusEnum, OccupancyStatusData, OccupancyStatusEnum, RoomStatusData, RoomStatusEnum } from "../../data/Enums";


interface IPartnerPmsContext {
    selectedOccupancyStatus: OccupancyStatusEnum[];
    selectedGovernanceStatus: GovernanceStatusEnum[];
    selectedRoomStatus: RoomStatusEnum[];
    selectedBlock: IBlock[];
    selectedFloor: IFloor[];
    selectedRoomType: IRoomType[];
    hasUserRooms: boolean;
    onlyUserRooms: boolean;

    handleChangeSelectedFilters: (
        occupancyStatus: OccupancyStatusEnum[],
        governanceStatus: GovernanceStatusEnum[],
        roomStatus: RoomStatusEnum[],
        block: IBlock[],
        floor: IFloor[],
        roomType: IRoomType[],
        onlyUserRooms: boolean
    ) => void;

    rooms: IRoom[];
    filteredRooms: IRoom[];
    handleRefreshRooms: (callback?: (rooms: IRoom[]) => void) => void;

    expectedOccupancy: IExpectedOccupancy[];
    handleRefreshExpectedOccupancy: () => void;

    expectedCheckInWithoutRoom: IReservation[];
    handleRefreshExpectedCheckInWithoutRoom: () => void;
}


const PartnerPmsContext = createContext<IPartnerPmsContext | undefined>(undefined);

const PartnerPmsContextProvider = (props: React.PropsWithChildren) => {
    const { enterprise } = useUserContext();
    
    const [selectedOccupancyStatus, setSelectedOccupancyStatus] = useState<OccupancyStatusEnum[]>([]);
    const [selectedGovernanceStatus, setSelectedGovernanceStatus] = useState<GovernanceStatusEnum[]>([]);
    const [selectedRoomStatus, setSelectedRoomStatus] = useState<RoomStatusEnum[]>([]);
    const [selectedBlock, setSelectedBlock] = useState<IBlock[]>([]);
    const [selectedFloor, setSelectedFloor] = useState<IFloor[]>([]);
    const [selectedRoomType, setSelectedRoomType] = useState<IRoomType[]>([]);
    const [onlyUserRooms, setOnlyUserRooms] = useState<boolean>(true);

    const [rooms, setRooms] = useState<IRoom[]>([]);
    const [expectedOccupancy, setExpectedOccupancy] = useState<IExpectedOccupancy[]>([]);
    const [expectedCheckInWithoutRoom, setExpectedCheckInWithoutRoom] = useState<IReservation[]>([]);


    useEffect(() => {
        handleChangeSelectedFilters([], [], [], [], [], [], true);
        // eslint-disable-next-line
    }, [enterprise]);


    const handleChangeSelectedFilters = useCallback((
        occupancyStatus: OccupancyStatusEnum[],
        governanceStatus: GovernanceStatusEnum[],
        roomStatus: RoomStatusEnum[],
        block: IBlock[],
        floor: IFloor[],
        roomType: IRoomType[],
        onlyUserRooms: boolean
    ) => {
        setSelectedOccupancyStatus(occupancyStatus);
        setSelectedGovernanceStatus(governanceStatus);
        setSelectedRoomStatus(roomStatus);
        setSelectedBlock(block);
        setSelectedFloor(floor);
        setSelectedRoomType(roomType);

        setOnlyUserRooms(onlyUserRooms);
        // eslint-disable-next-line
    }, [enterprise]);


    const handleRefreshRooms = useCallback((callback = (rooms: IRoom[]) => {}) => {
        const formData = FormHelper.Create(undefined);

        PartnerPmsService.ListRoomOverview(formData).then(resp => {
            const rooms = resp.data.rooms as IRoom[];
            setRooms(rooms);
            callback(rooms);
        }).catch(() => {
            setRooms([]);
            callback([]);
        });
        // eslint-disable-next-line
    }, []);


    const handleRefreshExpectedCheckInWithoutRoom = useCallback(() => {
        const formData = FormHelper.Create(undefined);

        PartnerPmsService.ListExpectedCheckInWithoutRoom(formData).then(resp => {
            setExpectedCheckInWithoutRoom(resp.data.reservations as IReservation[]);
        }).catch(() => {
            setExpectedCheckInWithoutRoom([]);
        });
        // eslint-disable-next-line
    }, []);


    const handleRefreshExpectedOccupancy = useCallback(() => {
        const formData = FormHelper.Create(undefined);
        
        PartnerPmsService.GetExpectedOccupancy(formData).then(resp => {
            setExpectedOccupancy(resp.data.expectedOccupancy as IExpectedOccupancy[]);
        }).catch(() => {
            setExpectedOccupancy([]);
        });
        // eslint-disable-next-line
    }, []);

    
    const data: IPartnerPmsContext = {
        selectedOccupancyStatus,
        selectedGovernanceStatus,
        selectedRoomStatus,
        selectedBlock,
        selectedFloor,
        selectedRoomType,
        hasUserRooms: (enterprise?.partnerPms.userRooms ?? []).length > 0,
        onlyUserRooms,
        handleChangeSelectedFilters,
        
        rooms: rooms,
        filteredRooms: useMemo(() => {
            let data = rooms;

            const hasUserRooms = (enterprise?.partnerPms.userRooms ?? []).length > 0;
            if (hasUserRooms && onlyUserRooms) {
                const userRoomsIds = enterprise?.partnerPms.userRooms.map(r => r.id) ?? [];
                data = data.filter(r => userRoomsIds.includes(r.id));
            }
    
            if (selectedOccupancyStatus.length > 0) {
                const occupancyStatus = Object.values(OccupancyStatusData).filter(s => selectedOccupancyStatus.includes(s.key)).map(s => s.partnerId ?? '');
                data = data.filter(r => occupancyStatus.includes(r.occupancyStatusId));
            }
    
            if (selectedGovernanceStatus.length > 0) {
                const governanceStatus = Object.values(GovernanceStatusData).filter(s => selectedGovernanceStatus.includes(s.key)).map(s => s.partnerId ?? '');
                data = data.filter(r => governanceStatus.includes(r.governanceStatusId));
            }
    
            if (selectedRoomStatus.length > 0) {
                const roomStatus = Object.values(RoomStatusData).filter(s => selectedRoomStatus.includes(s.key)).map(s => s.partnerId ?? '');
                data = data.filter(r => {
                    return roomStatus.some(s => {
                        const roomKey = s as keyof(IRoom);
                        const reservationKey = s as keyof(IReservation);
                        return r[roomKey] === true || (r.checkInReservation && r.checkInReservation[reservationKey] === true);
                    });
                });
            }
    
            if (selectedBlock.length > 0) {
                const block = selectedBlock.map(b => b.id);
                data = data.filter(r => block.includes(r.blockId));
            }
    
            if (selectedFloor.length > 0) {
                const floor = selectedFloor.map(f => f.id);
                data = data.filter(r => floor.includes(r.floorId));
            }
    
            if (selectedRoomType.length > 0) {
                const roomType = selectedRoomType.map(r => r.id);
                data = data.filter(r => roomType.includes(r.roomTypeId));
            }

            return data;
            // eslint-disable-next-line
        }, [enterprise, rooms, onlyUserRooms, selectedOccupancyStatus, selectedGovernanceStatus, selectedRoomStatus, selectedBlock, selectedFloor, selectedRoomType]),
        handleRefreshRooms,

        expectedOccupancy,
        handleRefreshExpectedOccupancy,
        
        expectedCheckInWithoutRoom,
        handleRefreshExpectedCheckInWithoutRoom
    }

    return (
        <PartnerPmsContext.Provider value={data}>
            {props.children}
        </PartnerPmsContext.Provider>
    );
}


const usePartnerPmsContext = () => {
    const context = useContext(PartnerPmsContext);

    if (context === undefined) {
        throw new Error('usePartnerPmsContext must be used within a PartnerPmsContextProvider');
    }

    return context;
}

export {
    usePartnerPmsContext
}

export default PartnerPmsContextProvider;